/**
 * Bridge between userSettings (string-valued) and the runtime registry
 * (typed RuntimePluginConfig[]).
 *
 * Why not store the typed shape directly in UserSettings? UserSettings
 * values round-trip through a SQL varchar column keyed by {user, name}
 * — one row per setting — so every value is a string. Settings that are
 * conceptually structured (reportHeader, reportMacros, reportPageFormat)
 * follow the same JSON-stringify-on-write / parse-on-read pattern.
 */

import { loadUserSettings, saveUserSettings, SettingScope } from '../../utils/userSettings';
import type { RuntimePluginConfig } from './types';

const PLUGINS_KEY = 'plugins';

/** Return the VENDOR allowlist of plugin ids this deployment can activate.
 *  Vendor sets this in userSettings.allowedPluginIds at install time; admin
 *  can't edit it from the UI. Empty = nothing allowed. */
export async function loadAllowedPluginIds(force = false): Promise<Set<string>> {
  const s = await loadUserSettings(force);
  if (!s.allowedPluginIds) return new Set();
  return new Set(
    s.allowedPluginIds
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
  );
}

/** Read + parse the configured plugins list. Returns [] if nothing stored
 *  or the stored JSON is malformed (defensive — a bad entry shouldn't
 *  break Settings; the admin can wipe it and re-enter). */
export async function loadPluginConfigs(force = false): Promise<RuntimePluginConfig[]> {
  const settings = await loadUserSettings(force);
  const raw = settings.plugins;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is RuntimePluginConfig =>
      typeof p === 'object' && p !== null && typeof (p as any).id === 'string' && typeof (p as any).baseUrl === 'string',
    );
  } catch {
    console.warn('[plugins] userSettings.plugins failed to parse — ignoring');
    return [];
  }
}

/** Persist a plugin list to clinic-wide scope. Rules + credentials + enabled
 *  flags are all practice-level choices, not per-reader. */
export async function savePluginConfigs(
  configs: RuntimePluginConfig[],
  scope: SettingScope = 'clinic',
): Promise<boolean> {
  return saveUserSettings({ plugins: JSON.stringify(configs) } as any, scope);
}

/** Mutate a single entry in-place — convenience for Settings UI. */
export async function updatePluginConfig(
  id: string,
  patch: Partial<RuntimePluginConfig>,
  scope: SettingScope = 'clinic',
): Promise<boolean> {
  const current = await loadPluginConfigs(true);
  const idx = current.findIndex((c) => c.id === id);
  if (idx === -1) return false;
  current[idx] = { ...current[idx], ...patch };
  return savePluginConfigs(current, scope);
}
